import { DropDown as DropDownHulu, DropDownItem, DropDownProps } from '@hulu-react-style-components/dropdown';
import React, { useMemo } from 'react';

import InputLabel from '../InputLabel';
import { Option } from '../types';
import { DropDownItemButton } from './styled';

export type Props<T = string> = DropDownProps & {
  options?: Option<T>[];
  isClearable?: boolean;
  createNewOption?: boolean;
};

const DropDown = <T extends string>({
  label,
  options,
  isClearable = false,
  createNewOption,
  onChange,
  value,
  ...rest
}: Props<T>): JSX.Element => {
  const Label = label && <InputLabel key="inputLabel">{label}</InputLabel>;

  const finalOptions = useMemo(() => getOptionsWithUniqueTooltips(options), [options]);
  const optionsValueMap = useMemo(() => getOptionsValueMap(finalOptions), [options]);
  const optionsTooltipMap = useMemo(() => getOptionsTooltipMap(finalOptions), [options]);

  value = getOptionTooltipValue(optionsValueMap.get(value ?? ''));

  // Maps the chosen option's label back to the option's value so that the original onChange handler
  // will be called with the correct selected value.
  const onChangeMapping = (selectedLabel: string) => {
    if (onChange) {
      const selectedValue = optionsTooltipMap.get(selectedLabel)?.value ?? selectedLabel;
      onChange(selectedValue);
    }
  };

  return (
    <DropDownHulu {...rest} value={value} onChange={onChangeMapping} label={Label} isClearable={isClearable}>
      {finalOptions?.map(({ label, value, tooltip }) => (
        <DropDownItem value={tooltip ?? label} key={value} data-testid="dropdownItem">
          {label}
        </DropDownItem>
      ))}
      {createNewOption && (
        <DropDownItemButton value="newOption" key="newOption" data-testid="dropdownItem">
          Create New Payment Method
        </DropDownItemButton>
      )}
    </DropDownHulu>
  );
};

const getOptionsWithUniqueTooltips = (options: Option[] | undefined) => {
  // Calculate how many of each tooltip is present.
  const totalTooltipCounts = new Map<string, number>();
  options?.forEach((option) => {
    const tooltip = getOptionTooltipValue(option);
    const currentCount = totalTooltipCounts.get(tooltip) ?? 0;
    totalTooltipCounts.set(tooltip, currentCount + 1);
  });

  // If there are any duplicates, have to distinguish them by appending the value in parens.
  // e.g. tooltip (993fadf5-88e7-4bdf-8deb-1d881bedccd3), tooltip (81188942-ac51-471c-b9c0-a22c9ab5e89e)
  const containsDuplicates = options && totalTooltipCounts.size < options?.length;
  if (containsDuplicates) {
    return options?.map((option) => {
      const tooltip = getOptionTooltipValue(option);
      const isDuplicateTooltip = (totalTooltipCounts.get(tooltip) ?? 0) > 1;
      if (isDuplicateTooltip) {
        return {
          ...option,
          tooltip: `${tooltip} (${option.value})`,
        };
      } else {
        return option;
      }
    });
  } else {
    return options;
  }
};

// returns map of:
// key: value
// value: option with that as its value
const getOptionsValueMap = (options: Option[] | undefined) => {
  const optionsValueMap = new Map<string, Option>();
  options?.forEach((option) => {
    optionsValueMap.set(option.value, option);
  });

  return optionsValueMap;
};

// returns map of:
// key: tooltip
// value: option with that as its tooltip
const getOptionsTooltipMap = (options: Option[] | undefined) => {
  const optionsTooltipValueMap = new Map<string, Option>();
  options?.forEach((option) => {
    optionsTooltipValueMap.set(getOptionTooltipValue(option), option);
  });

  return optionsTooltipValueMap;
};

const getOptionTooltipValue = (option?: Option): string => {
  return option?.tooltip ?? option?.label ?? '';
};

export default DropDown;
